import React, { FC } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useResponsiveType } from '@agria/theme/src/utils';
import { Link } from '@agria/theme/src/components/link/Link';
import { Container } from '@agria/theme/src/components/container/Container';
import { LinkTo } from '@agria/theme/src/components/linkTo/LinkTo';
import { ArrowLeft } from '@agria/theme/src/components/icon';
import type { NavigationItem, SecondaryNavigationItem } from '../header/Header';
import tokens from './tokens';

interface TopLevelNavProps {
  activePage?: NavigationItem | null;
  enableMyAccount?: boolean;
  isMyAccount?: boolean;
  navigationItems: NavigationItem[];
  secondaryNavigationItems: SecondaryNavigationItem[];
}

const StyledTopLevelNav = styled.div`
  ${({ theme: { colors, space, maxMedia } }) => css`
    background-color: ${colors.brandOne100};

    > div {
      display: flex;
      justify-content: space-between;
    }

    ul {
      display: flex;
    }

    a {
      padding: ${space.small};
      color: ${colors.brandOne20};
      text-decoration: none;
      display: inline-flex;
      ${useResponsiveType('bodySmall')};
      line-height: 24px; // Align heights of links with icons

      &:hover {
        color: ${colors.utilityWhite};
      }
    }

    nav:first-of-type a[aria-current],
    nav:first-of-type:not(:has([aria-current])) li:first-of-type a {
      background-color: ${colors.utilityWhite};
      color: ${colors.brandOne100};
      cursor: default;
    }

    nav:last-of-type a {
      padding: ${space.small} ${space.xSmall};
    }

    @media (max-width: ${maxMedia.large}) {
      display: none;
    }
  `};
`;

export const TopLevelNav: FC<TopLevelNavProps> = ({
  activePage,
  enableMyAccount,
  isMyAccount,
  navigationItems,
  secondaryNavigationItems,
}) => {
  console.log('tokens', tokens);
  if (!tokens.includeTopLevelNav) {
    return null;
  }

  return (
    <StyledTopLevelNav>
      <Container>
        {!activePage?.hideTopLevelNavigation && !isMyAccount ? (
          <>
            <nav aria-label="Main Navigation">
              {navigationItems && navigationItems.length > 1 && (
                <ul>
                  {navigationItems
                    .filter((item) => !item.hideTopLevelNavigation)
                    .map((item, index) => (
                      <li key={index}>
                        <LinkTo to={item.link.url}>{item.link.label}</LinkTo>
                      </li>
                    ))}
                </ul>
              )}
            </nav>
            <nav aria-label="Secondary Navigation">
              <ul>
                {secondaryNavigationItems?.map((item, index) => (
                  <li key={index}>
                    <LinkTo to={item.link.url}>{item.link.label}</LinkTo>
                  </li>
                ))}
                {enableMyAccount && (
                  <li>
                    <Link url="/my-account/login/" label="My Account" />
                  </li>
                )}
              </ul>
            </nav>
          </>
        ) : (
          <Link
            url="/"
            label="Back to the main site"
            icon={<ArrowLeft size="small" />}
          />
        )}
      </Container>
    </StyledTopLevelNav>
  );
};
